import { useToast } from "@chakra-ui/react";
import React from "react";

import { successToast } from "../../../components";
import { CallFragment, CallNoteType, useCallShareQuery } from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import MediaPlayer, { MediaPlayerProps } from "../MediaPlayer/MediaPlayer";
import {
  MEDIA_VIDEO_HEIGHT_RATIO,
  MediaPlayerV2,
  MediaPlayerV2Props,
} from "../MediaPlayerV2/MediaPlayerV2";
import useCallNotesV3 from "./useCallNotesV3";
import useCallTranscript from "./useCallTranscript";

type CallVideoProps = Omit<
  MediaPlayerProps,
  | "speakers"
  | "duration"
  | "onAddNote"
  | "notes"
  | "segments"
  | "shareableUsers"
  | "shareableUsersLoading"
> &
  Pick<MediaPlayerV2Props, "showVideo"> & {
    call: CallFragment;
    notesForCurrentUserOnly: boolean;
  };

const CallVideo: React.FC<CallVideoProps> = ({
  call,
  notesForCurrentUserOnly,
  showVideo,
  ...videoProps
}) => {
  const toast = useToast();
  const useMediaPlayerV2 = useFeatureFlag("media-player-v2");

  const { onAddNote } = useCallNotesV3({
    call,
    notesForCurrentUserOnly,
    onAddCallNoteCompleted(data) {
      const note = data.addNewCallNote?.callNote;
      if (note) {
        successToast(
          toast,
          `${note.type === CallNoteType.Note ? "Note" : "Reaction"} added`,
          { position: "top" }
        );
      }
    },
  });

  const { data: transcriptData } = useCallTranscript({
    callId: call.id,
    notesForCurrentUserOnly,
  });
  const { notesToHighlight: notes } = transcriptData;

  const shareQuery = useCallShareQuery({ variables: { id: call.id } });

  const sharedProps = {
    ...videoProps,
    callId: call.id,
    speakers: call.speakers,
    canChangeSpeaker: call.canEdit,
    duration: call.duration,
    onAddNote,
    notes: notes ?? [],
    segments: transcriptData.transcript,
    shareableUsers: shareQuery.data?.call?.shareableUsers,
    shareableUsersLoading: shareQuery.loading,
  };

  if (useMediaPlayerV2) {
    return (
      <MediaPlayerV2
        {...sharedProps}
        showVideo={showVideo}
        heightRatio={MEDIA_VIDEO_HEIGHT_RATIO}
      />
    );
  }

  return <MediaPlayer {...sharedProps} />;
};

export default CallVideo;
